import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { VigilarCuentaButton } from '../components/vigilancia/VigilarCuentaButton';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  ArrowLeft, User, AlertTriangle, Activity, FileText, 
  Play, Plus, DollarSign, Calendar, TrendingUp, Shield,
  ClipboardList, Save, RefreshCw, CheckCircle, XCircle,
  CreditCard, Star, Trash2, Edit2, Briefcase, Eye, Share2,
  FolderOpen, UserCheck, Maximize2, Minimize2, Link2, ExternalLink, Building2, Download,
  Receipt, ShieldOff
} from 'lucide-react';
import { getSeverityColor } from '../utils/styleUtils';
import DocumentManager from '../components/DocumentManager';
import ClienteLegajoDocumentos from '../components/ClienteLegajoDocumentos';
import ClienteEstadoManager from '../components/ClienteEstadoManager';
import { useAuth } from '../contexts/AuthContext';
import { ClienteCvusTabla } from '../components/padron/ClienteCvusTabla';
import { VincularReemplazoDialog } from '../components/padron/VincularReemplazoDialog';
import { ClienteListaBlanca } from '../components/account-lists/ClienteListaBlanca';
import { ClienteFranjaRiesgo } from '../components/cliente/ClienteFranjaRiesgo';
import { ClienteCuentas, ClienteDatosIncompletos } from '../components/cliente/ClienteCuentas';
import { ClienteMovimientos } from '../components/cliente/ClienteMovimientos';
import { ClienteAnalisis } from '../components/cliente/ClienteAnalisis';
import EmpresaDetalle from './EmpresaDetalle';
import { AccessDeniedPage } from '../components/ProtectedRoute';

const SEVERITIES = ['BAJA', 'MEDIA', 'ALTA', 'CRITICA'];
const hoyISO = () => new Date().toISOString().slice(0, 10);
const haceDias = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

export const ClienteDetalle = () => {
  const { cuit } = useParams();
  const navigate = useNavigate();
  const [cliente, setCliente] = useState(null);
  const [alertas, setAlertas] = useState([]);
  const [movimientos, setMovimientos] = useState([]);
  const [monitors, setMonitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('resumen');
  // Partes B-D: ficha única por CUIT (franja, cuentas, datos incompletos) y rango compartido Movimientos/Análisis
  const cuitDigitos = String(cuit || '').replace(/\D/g, '');
  const [ficha, setFicha] = useState(null);
  const [rango, setRango] = useState({ desde: haceDias(90), hasta: hoyISO() });
  
  // Estados para modales
  const [isCreateAlertOpen, setIsCreateAlertOpen] = useState(false);
  const [isRunMonitorOpen, setIsRunMonitorOpen] = useState(false);
  const [selectedMonitor, setSelectedMonitor] = useState(null);
  const [runningMonitor, setRunningMonitor] = useState(false);
  const [savingAlert, setSavingAlert] = useState(false);
  
  // Formulario de nueva alerta
  const [newAlert, setNewAlert] = useState({
    titulo: '',
    descripcion: '',
    severidad: 'MEDIA'
  });

  // MRC States
  const [mrcFactores, setMrcFactores] = useState({});
  const [mrcAtributos, setMrcAtributos] = useState({});
  const [mrcScore, setMrcScore] = useState(null);
  const [savingMRC, setSavingMRC] = useState(false);
  const [calculatingMRC, setCalculatingMRC] = useState(false);
  
  // Fullscreen mode
  const [isFullscreen, setIsFullscreen] = useState(false);

  // CVU States
  const [cvus, setCvus] = useState([]);
  const [isAddCvuOpen, setIsAddCvuOpen] = useState(false);
  const [savingCvu, setSavingCvu] = useState(false);
  const [newCvu, setNewCvu] = useState({ cvu: '', alias: '', tipo: 'SECUNDARIO' });
  const [cvuReemplazo, setCvuReemplazo] = useState(null);
  const { user } = useAuth();
  const esAdmin = ['ADMIN', 'SUPERADMIN', 'ADMINISTRADOR'].includes((user?.rol || '').toUpperCase());

  // Movimientos Selection States
  const [selectedMovimientos, setSelectedMovimientos] = useState(new Set());
  const [isCreateAlertFromMovOpen, setIsCreateAlertFromMovOpen] = useState(false);
  const [alertFromMov, setAlertFromMov] = useState({ titulo: '', descripcion: '', severidad: 'MEDIA' });
  
  // Waya data update
  const [updatingWaya, setUpdatingWaya] = useState(false);
  const [wayaJobId, setWayaJobId] = useState(null);

  // Filtro de CVU para movimientos
  const [selectedCvuFilter, setSelectedCvuFilter] = useState('todos');
  const [allMovimientos, setAllMovimientos] = useState([]); // Guarda todos los movimientos sin filtrar

  // Cases States
  const [casos, setCasos] = useState([]);
  const [isCreateCaseFromMovOpen, setIsCreateCaseFromMovOpen] = useState(false);
  const [caseFromMov, setCaseFromMov] = useState({ 
    titulo: '', 
    descripcion: '', 
    prioridad: 'MEDIA',
    tipo: 'ALERTA'
  });
  const [savingCase, setSavingCase] = useState(false);

  // Empresa asociada
  const [empresaData, setEmpresaData] = useState(null);
  const [loadingEmpresa, setLoadingEmpresa] = useState(false);

  // Edición de cliente
  const [isEditClienteOpen, setIsEditClienteOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [savingCliente, setSavingCliente] = useState(false);

  // Impuesto al cheque (fuente única: impuesto_cheque_juridicas)
  const [icEstado, setIcEstado] = useState(null);
  const [icSaving, setIcSaving] = useState(false);

  const [fichaDenegada, setFichaDenegada] = useState(false);
  useEffect(() => {
    setFichaDenegada(false);
    api.get(`/padron-interno/cliente/${cuitDigitos}/ficha`, { timeout: 120000 })
      .then((r) => setFicha(r.data))
      .catch((e) => { setFicha(null); setFichaDenegada(e.response?.status === 403); });
  }, [cuitDigitos]);

  useEffect(() => {
    if (ficha && !ficha.en_padron && empresaData) setActiveTab('empresa');
  }, [ficha, empresaData]);

  useEffect(() => {
    loadClienteData();
    loadMRCData();
    loadCasosRelacionados();
    loadEmpresaData();
    loadImpuestoCheque();
  }, [cuit]);

  // Cargar CVUs cuando tengamos el cliente
  useEffect(() => {
    if (cliente?.cvus) {
      // Si el cliente ya trae los CVUs del padrón interno, usarlos directamente
      setCvus(cliente.cvus);
    } else if (cliente?.id) {
      // Fallback: cargar desde el endpoint de cliente-cvus
      loadCvus();
    }
  }, [cliente?.id, cliente?.cvus]);

  const loadImpuestoCheque = async () => {
    try {
      const r = await api.get(`/padron-interno/cliente/${cuit}/impuesto-cheque`);
      setIcEstado(r.data);
    } catch (e) { /* noop */ }
  };

  const toggleImpuestoCheque = async (value) => {
    if (!icEstado?.es_admin) return;
    setIcSaving(true);
    const prev = icEstado;
    setIcEstado({ ...icEstado, incluir: value });
    try {
      await api.put(`/padron-interno/cliente/${cuit}/impuesto-cheque`, { incluir: value });
      await loadImpuestoCheque();
      toast.success(value ? 'La cuenta ahora ENTRA al impuesto al cheque' : 'La cuenta quedó EXCLUIDA del impuesto al cheque');
    } catch (e) {
      setIcEstado(prev);
      toast.error('No se pudo actualizar el impuesto al cheque');
    } finally { setIcSaving(false); }
  };

  const loadClienteData = async () => {
    setLoading(true);
    try {
      // Cargar datos del cliente desde padrón interno (primero, necesario para CVUs)
      const padronResponse = await api.get(`/padron-interno/cliente/${cuit}`);
      if (padronResponse.data) {
        setCliente(padronResponse.data);
      }

      // Cargar el resto en paralelo para evitar que uno bloquee a los demás
      const clienteCvus = padronResponse.data?.cvus || [];
      const cvuNumbers = clienteCvus
        .map(c => c.numero || c.numero_limpio || c.cvu)
        .filter(Boolean)
        .join(',');
      const movParams = { limit: 500 };
      if (cvuNumbers) movParams.cvus = cvuNumbers;
      if (cuit) movParams.cuit = cuit;

      const [alertsRes, movRes, monitorsRes] = await Promise.allSettled([
        api.get('/alerts?limit=100'),
        api.get('/movimientos', { params: movParams }),
        api.get('/monitors'),
      ]);

      if (alertsRes.status === 'fulfilled') {
        const clienteAlertas = (alertsRes.value.data || []).filter(
          alert => alert.cuenta_id === cuit || alert.cuenta_id === String(cuit)
        );
        setAlertas(clienteAlertas);
      }
      if (movRes.status === 'fulfilled') {
        setAllMovimientos(movRes.value.data || []);
        setMovimientos(movRes.value.data || []);
      }
      if (monitorsRes.status === 'fulfilled') {
        setMonitors((monitorsRes.value.data || []).filter(m => m.activo));
      }

    } catch (error) {
      console.error('Error cargando datos del cliente:', error);
      toast.error('Error al cargar datos del cliente');
    } finally {
      setLoading(false);
    }
  };

  const loadCasosRelacionados = async () => {
    try {
      const casesRes = await api.get(`/cases/por-cliente/${encodeURIComponent(cuit)}`);
      setCasos(casesRes.data.casos || []);
    } catch (error) {
    }
  };

  const loadEmpresaData = async () => {
    setLoadingEmpresa(true);
    try {
      const res = await api.get(`/padron-interno/cliente/${encodeURIComponent(cuit)}/empresa-data`);
      if (res.data?.empresa_exists) {
        setEmpresaData(res.data);
      }
    } catch (error) {
      // No empresa data
    } finally {
      setLoadingEmpresa(false);
    }
  };

  const openEditCliente = () => {
    setEditForm({
      razon_social: cliente.razon_social || '',
      dni: cliente.dni || '',
      email: cliente.email || cliente.email_waya || '',
      fecha_nacimiento: cliente.fecha_nacimiento || '',
      nacionalidad: cliente.nacionalidad || '',
      genero: cliente.genero || '',
      tipo: cliente.tipo || cliente.persona || '',
      pep_uif: cliente.pep_uif || cliente.pep === 'SI' || false,
      telefono: cliente.telefono || '',
    });
    setIsEditClienteOpen(true);
  };

  const handleSaveCliente = async () => {
    setSavingCliente(true);
    try {
      await api.put(`/padron-interno/cliente/${cuit}/datos`, editForm);
      toast.success('Cliente actualizado correctamente');
      setIsEditClienteOpen(false);
      loadClienteData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSavingCliente(false);
    }
  };


  const handleCreateCaseFromMovimientos = async () => {
    if (!caseFromMov.titulo.trim()) {
      toast.error('El título es requerido');
      return;
    }

    setSavingCase(true);
    try {
      // Crear el caso
      const caseData = {
        titulo: caseFromMov.titulo,
        descripcion: caseFromMov.descripcion || `Caso creado desde movimientos del cliente ${cuit}`,
        prioridad: caseFromMov.prioridad,
        tipo: caseFromMov.tipo,
        cuentas_relacionadas: [cuit],
        movimientos_ids: Array.from(selectedMovimientos)
      };
      
      await api.post('/cases', caseData);
      toast.success('Caso creado exitosamente');
      setIsCreateCaseFromMovOpen(false);
      setSelectedMovimientos(new Set());
      setCaseFromMov({ titulo: '', descripcion: '', prioridad: 'MEDIA', tipo: 'ALERTA' });
      loadCasosRelacionados();
    } catch (error) {
      console.error('Error creating case:', error);
      toast.error('Error al crear el caso');
    } finally {
      setSavingCase(false);
    }
  };

  const loadMRCData = async () => {
    try {
      // Cargar factores y opciones MRC
      const opcionesRes = await api.get('/mrc/opciones');
      setMrcFactores(opcionesRes.data);

      // Cargar score MRC automáticamente
      try {
        const scoreRes = await api.get(`/mrc/clientes/${cuit}/score`);
        setMrcScore(scoreRes.data);
      } catch (e) {
      }

      // Cargar atributos del cliente si existen
      try {
        const atributosRes = await api.get(`/mrc/clientes/${cuit}/atributos`);
        if (atributosRes.data && !atributosRes.data.mensaje) {
          setMrcAtributos(atributosRes.data);
        }
      } catch (e) {
        // Cliente sin atributos MRC
      }

      // Intentar cargar score si ya existe
      try {
        const scoreRes = await api.get(`/mrc/clientes/${cuit}/score`);
        if (scoreRes.data && scoreRes.data.score_mrc !== null) {
          setMrcScore(scoreRes.data);
        }
      } catch (e) {
        // Sin score calculado
      }
    } catch (error) {
      console.error('Error cargando MRC:', error);
    }
  };

  // Funciones de CVU
  const loadCvus = async () => {
    if (!cliente?.id) return;
    try {
      const res = await api.get(`/cliente-cvus/cliente/${cliente.id}`);
      setCvus(res.data);
    } catch (error) {
      console.error('Error cargando CVUs:', error);
    }
  };

  const handleAddCvu = async () => {
    if (!newCvu.cvu.trim()) {
      toast.error('El CVU es requerido');
      return;
    }
    
    setSavingCvu(true);
    try {
      await api.post(`/padron-interno/cliente/${cuit}/cvu`, newCvu);
      toast.success('CVU agregado correctamente');
      setIsAddCvuOpen(false);
      setNewCvu({ cvu: '', alias: '', tipo: 'SECUNDARIO' });
      loadClienteData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al agregar CVU');
    } finally {
      setSavingCvu(false);
    }
  };

  const handleSetPrincipal = async (cvuNumero) => {
    try {
      await api.post(`/padron-interno/cliente/${cuit}/cvu/${cvuNumero}/set-principal`);
      toast.success('CVU marcado como principal');
      loadClienteData();
    } catch (error) {
      toast.error('Error al cambiar CVU principal');
    }
  };

  const handleDeleteCvu = async (cvuNumero) => {
    if (!confirm('¿Eliminar este CVU?')) return;
    
    try {
      await api.delete(`/padron-interno/cliente/${cuit}/cvu/${cvuNumero}`);
      toast.success('CVU eliminado');
      loadClienteData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar CVU');
    }
  };

  // Toggle activo/inactivo de CVU
  const handleToggleCvuActivo = async (cvuNumero, currentActivo) => {
    try {
      // Actualizar en la BD del padrón interno
      await api.put(`/padron-interno/cliente/${cuit}/cvu/${cvuNumero}/toggle-activo`, {
        activo: !currentActivo
      });
      
      // Actualizar estado local
      setCvus(prevCvus => prevCvus.map(c => {
        const num = c.numero || c.numero_limpio || c.cvu;
        if (num === cvuNumero) {
          return { ...c, activo: !currentActivo, estado: !currentActivo ? 'Activo' : 'Inactivo' };
        }
        return c;
      }));
      
      toast.success(`CVU ${!currentActivo ? 'activado' : 'desactivado'}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar CVU');
    }
  };

  // Filtrar movimientos por CVU seleccionado
  const handleCvuFilterChange = (cvuValue) => {
    setSelectedCvuFilter(cvuValue);
    setSelectedMovimientos(new Set()); // Limpiar selección al cambiar filtro
    
    if (cvuValue === 'todos') {
      setMovimientos(allMovimientos);
    } else {
      const filtered = allMovimientos.filter(mov => 
        mov.cbu_cvu_origen === cvuValue || 
        mov.cbu_cvu_destino === cvuValue ||
        mov.cvu_mov === cvuValue
      );
      setMovimientos(filtered);
    }
  };

  const handleActualizarDatosWaya = async () => {
    setUpdatingWaya(true);
    try {
      const res = await api.post('/waya/actualizar-datos-cuentas', {
        cuit_limpio: cliente.cuit_limpio || cuit
      });
      if (res.data?.success) {
        setWayaJobId(res.data.job_id);
        toast.success(res.data.message);
        // Poll job status
        const pollInterval = setInterval(async () => {
          try {
            const jobRes = await api.get(`/waya/job/${res.data.job_id}`);
            if (jobRes.data?.estado === 'COMPLETADO') {
              clearInterval(pollInterval);
              setUpdatingWaya(false);
              setWayaJobId(null);
              toast.success(`Datos actualizados: ${jobRes.data.exitosos} exitosos, ${jobRes.data.errores} errores`);
              loadClienteData();
            }
          } catch { /* keep polling */ }
        }, 3000);
        // Safety: stop polling after 5 min
        setTimeout(() => { clearInterval(pollInterval); setUpdatingWaya(false); }, 300000);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al iniciar actualización Waya');
      setUpdatingWaya(false);
    }
  };

  // Funciones para selección de movimientos
  const toggleMovimientoSelection = (movId) => {
    const newSelection = new Set(selectedMovimientos);
    if (newSelection.has(movId)) {
      newSelection.delete(movId);
    } else {
      newSelection.add(movId);
    }
    setSelectedMovimientos(newSelection);
  };

  const toggleAllMovimientos = () => {
    if (selectedMovimientos.size === movimientos.slice(0, 20).length) {
      setSelectedMovimientos(new Set());
    } else {
      setSelectedMovimientos(new Set(movimientos.slice(0, 20).map(m => m.id_waya || m.id)));
    }
  };

  const openCreateAlertFromMovimientos = () => {
    const selected = movimientos.filter(m => selectedMovimientos.has(m.id_waya || m.id));
    const totalMonto = selected.reduce((sum, m) => sum + parseFloat(m.monto || 0), 0);
    
    setAlertFromMov({
      titulo: `Alerta por ${selected.length} movimiento(s) sospechoso(s)`,
      descripcion: `Movimientos seleccionados del cliente ${cuit}:\n` +
        `- Total: ${selected.length} movimiento(s)\n` +
        `- Monto total: $${totalMonto.toLocaleString()}\n\n` +
        `IDs: ${Array.from(selectedMovimientos).join(', ')}`,
      severidad: 'MEDIA'
    });
    setIsCreateAlertFromMovOpen(true);
  };

  const handleCreateAlertFromMovimientos = async () => {
    if (!alertFromMov.titulo.trim()) {
      toast.error('El título es requerido');
      return;
    }

    setSavingAlert(true);
    try {
      await api.post('/alerts/', {
        titulo: alertFromMov.titulo,
        descripcion: alertFromMov.descripcion,
        severidad: alertFromMov.severidad,
        cuenta_id: cuit,
        movimientos_ids: Array.from(selectedMovimientos)
      });
      toast.success('Alerta creada desde movimientos seleccionados');
      setIsCreateAlertFromMovOpen(false);
      setSelectedMovimientos(new Set());
      setAlertFromMov({ titulo: '', descripcion: '', severidad: 'MEDIA' });
      loadClienteData();
    } catch (error) {
      console.error('Error creating alert:', error);
      toast.error('Error al crear alerta');
    } finally {
      setSavingAlert(false);
    }
  };

  const handleCreateAlert = async () => {
    if (!newAlert.titulo.trim()) {
      toast.error('El título es requerido');
      return;
    }

    setSavingAlert(true);
    try {
      await api.post('/alerts/', {
        titulo: newAlert.titulo,
        descripcion: newAlert.descripcion || `Alerta manual para cliente ${cuit}`,
        severidad: newAlert.severidad,
        cuenta_id: cuit,
        monitor_id: null
      });
      toast.success('Alerta creada correctamente');
      setIsCreateAlertOpen(false);
      setNewAlert({ titulo: '', descripcion: '', severidad: 'MEDIA' });
      loadClienteData();
    } catch (error) {
      toast.error('Error al crear alerta');
    } finally {
      setSavingAlert(false);
    }
  };

  const handleRunMonitor = async () => {
    if (!selectedMonitor) {
      toast.error('Seleccione un monitor');
      return;
    }

    setRunningMonitor(true);
    try {
      // Ejecutar backtesting del monitor
      const response = await api.post(`/monitors/${selectedMonitor}/backtest`);
      const results = response.data;
      
      // Filtrar resultados solo para este cliente
      const clienteResults = results.coincidencias?.filter(
        c => c.cuenta_id === cuit || c.cuenta_id === String(cuit)
      ) || [];

      if (clienteResults.length > 0) {
        toast.success(`Monitor encontró ${clienteResults.length} coincidencia(s) para este cliente`);
        // Opcionalmente crear alerta automática
        const monitor = monitors.find(m => m.id === selectedMonitor);
        if (monitor) {
          await api.post('/monitors/historical-alert', {
            monitor_id: selectedMonitor,
            cuenta_id: cuit,
            titulo: `${monitor.nombre}: Detección en cliente ${cuit}`,
            descripcion: `Coincidencia detectada por monitor "${monitor.nombre}"`,
            movimientos_ids: clienteResults[0]?.movimientos_ids || []
          });
          toast.success('Alerta generada automáticamente');
          loadClienteData();
        }
      } else {
        toast.info('El monitor no encontró coincidencias para este cliente');
      }
      
      setIsRunMonitorOpen(false);
    } catch (error) {
      console.error('Error ejecutando monitor:', error);
      toast.error('Error al ejecutar monitor');
    } finally {
      setRunningMonitor(false);
    }
  };

  const handleSaveMRCAtributos = async () => {
    setSavingMRC(true);
    try {
      await api.post(`/mrc/clientes/${cuit}/atributos`, mrcAtributos);
      toast.success('Atributos MRC guardados');
    } catch (error) {
      toast.error('Error al guardar atributos');
    } finally {
      setSavingMRC(false);
    }
  };

  const handleCalculateMRC = async () => {
    setCalculatingMRC(true);
    try {
      const response = await api.get(`/mrc/clientes/${cuit}/score`);
      setMrcScore(response.data);
      if (response.data.score_mrc === null) {
        toast.info('Configure los atributos del cliente primero');
      } else {
        toast.success('Score MRC calculado');
      }
    } catch (error) {
      toast.error('Error al calcular score');
    } finally {
      setCalculatingMRC(false);
    }
  };

  const getMRCCategoryColor = (categoria) => {
    switch(categoria) {
      case 'BAJO': return { bg: '#dcfce7', text: '#166534' };
      case 'MEDIO': return { bg: '#fef3c7', text: '#b45309' };
      case 'ALTO': return { bg: '#fecaca', text: '#dc2626' };
      default: return { bg: '#f1f5f9', text: '#475569' };
    }
  };

  // Calcular estadísticas

  // Sin permiso para ver clientes (la ficha responde 403): no se muestran datos ni acciones.
  if (fichaDenegada) return <AccessDeniedPage />;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Cargando datos del cliente...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${isFullscreen ? 'fixed inset-0 z-50 bg-slate-50' : ''} p-6 lg:p-8 h-full overflow-auto bg-slate-50`}>
      {/* Header (pt-10: deja libre la barra fija de búsqueda y notificaciones de arriba a la derecha) */}
      <div className={`flex flex-wrap items-center justify-between gap-3 mb-6 ${isFullscreen ? '' : 'pt-10'}`}>
        <div className="flex items-center gap-4">
          {!isFullscreen && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/padron')}
              style={{ color: '#475569' }}
            >
              <ArrowLeft size={16} className="mr-1" />
              Volver
            </Button>
          )}
          {isFullscreen && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsFullscreen(false)}
              style={{ color: '#475569' }}
            >
              <Minimize2 size={16} className="mr-1" />
              Salir
            </Button>
          )}
          <div>
            <h1 className="text-2xl font-heading font-bold text-slate-900">
              {cliente?.razon_social || `Cliente ${cuit}`}
            </h1>
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-slate-600 font-mono" data-testid="ficha-cuit">CUIT: {cuit}</p>
              {ficha && (
                <>
                  <Badge variant="outline" className={ficha.juridica ? 'border-purple-200 text-purple-700' : 'border-blue-200 text-blue-700'} data-testid="ficha-tipo">{ficha.tipo}</Badge>
                  <Badge className={ficha.estado === 'ACTIVO' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'} data-testid="ficha-estado">{ficha.estado}</Badge>
                  {!ficha.en_padron && <Badge className="bg-amber-100 text-amber-800" data-testid="ficha-sin-padron">No está en el padrón</Badge>}
                </>
              )}
            </div>
            {cliente?.origen === 'reporte_usuarios_waya' && (
              <Badge variant="outline" className="mt-1 text-[11px] text-indigo-700 border-indigo-200" data-testid="cliente-origen-reporte">
                Alta desde Reporte de usuarios Waya
              </Badge>
            )}
            {(cliente?.estado_usuario_waya || cliente?.estado_waya) === 'Eliminado' && (
              <Badge variant="outline" className="mt-1 ml-1 text-[11px] font-semibold text-red-700 border-red-300 bg-red-50"
                title="Según el Reporte de usuarios de Waya" data-testid="cliente-eliminado-waya-badge">
                Eliminado en Waya
              </Badge>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setIsFullscreen(!isFullscreen)}
            variant="outline"
            size="sm"
            title={isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}
            style={{ color: '#475569' }}
          >
            {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          </Button>
          <Button
            onClick={handleActualizarDatosWaya}
            disabled={updatingWaya}
            variant="outline"
            style={{ color: '#059669', borderColor: '#a7f3d0' }}
            data-testid="btn-actualizar-waya"
          >
            <RefreshCw size={16} className={`mr-2 ${updatingWaya ? 'animate-spin' : ''}`} style={{ color: '#059669' }} />
            <span style={{ color: '#059669' }}>{updatingWaya ? 'Actualizando...' : 'Actualizar datos Waya'}</span>
          </Button>
          <VigilarCuentaButton termino={cuit} />
          <Button
            onClick={() => navigate(`/m/gestion-de-casos/network-analysis?cuit=${cuit}`)}
            variant="outline"
            style={{ color: '#7c3aed', borderColor: '#ddd6fe' }}
          >
            <Share2 size={16} className="mr-2" style={{ color: '#7c3aed' }} />
            <span style={{ color: '#7c3aed' }}>Ver Red</span>
          </Button>
          <Button
            onClick={() => setIsRunMonitorOpen(true)}
            variant="outline"
            style={{ color: '#9333ea', borderColor: '#e9d5ff' }}
          >
            <Play size={16} className="mr-2" style={{ color: '#9333ea' }} />
            <span style={{ color: '#9333ea' }}>Ejecutar Monitor</span>
          </Button>
          <Button
            onClick={() => setIsCreateAlertOpen(true)}
            className="bg-rose-600 hover:bg-rose-700 text-white"
            data-testid="cliente-crear-alerta-btn"
          >
            <Plus size={16} className="mr-2" />
            Crear Alerta
          </Button>
        </div>
      </div>

      {/* Banner Empresa Asociada */}
      {empresaData && (
        <Card className="p-4 bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200 mb-6" data-testid="empresa-link-banner">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Building2 size={18} className="text-emerald-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-emerald-900">
                  Empresa asociada: "{empresaData.razon_social}"
                </p>
                <p className="text-xs text-emerald-600">
                  Estado: {empresaData.estado || 'N/A'} | 
                  Docs: {empresaData.documentos_obligatorios_aprobados}/{empresaData.documentos_obligatorios_total} obligatorios | 
                  Cuentas Waya: {empresaData.cuentas_vinculadas_total}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-100"
              onClick={() => navigate(`/m/clientes/empresa/${empresaData.id}`)}
              data-testid="btn-ver-empresa"
            >
              <ExternalLink size={14} className="mr-2" />
              Ver Empresa
            </Button>
          </div>
        </Card>
      )}

      {/* B1: franja de riesgo y B4: datos incompletos */}
      <ClienteFranjaRiesgo franja={ficha?.franja} />
      <ClienteDatosIncompletos faltantes={ficha?.faltantes} />

      {/* Tabs */}
      <div className="flex gap-2 mb-4 border-b border-slate-200 overflow-x-auto">
        {[
          { key: 'resumen', label: 'Resumen', icon: User },
          { key: 'estado', label: 'Estado', icon: UserCheck },
          { key: 'mrc', label: 'Score MRC', icon: ClipboardList },
          { key: 'alertas', label: 'Alertas', icon: AlertTriangle },
          { key: 'casos', label: 'Casos', icon: Briefcase },
          { key: 'movimientos', label: 'Movimientos', icon: Activity },
          { key: 'analisis', label: 'Análisis', icon: TrendingUp },
          ...(empresaData ? [{ key: 'empresa', label: 'Empresa (documentación, representante, cuentas, límites)', icon: Building2 }] : []),
          { key: 'legajo', label: 'Legajo', icon: FolderOpen },
          { key: 'documentos', label: 'Documentos', icon: FileText },
        ].map(tab => (
          <button
            key={tab.key}
            data-testid={`ficha-tab-${tab.key}`}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.key
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <tab.icon size={16} />
            {tab.label}
            {tab.key === 'casos' && casos.length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-xs rounded-full bg-purple-100 text-purple-700">
                {casos.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'resumen' && cliente && (
        <div className="space-y-6">
          <Card className="p-6 bg-white border-slate-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-slate-900">Información del Cliente</h2>
              <Button variant="outline" size="sm" onClick={openEditCliente} data-testid="edit-cliente-btn">
                <Edit2 size={14} className="mr-1" />
                Editar
              </Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Razón Social</p>
                <p className="font-medium text-slate-900">{cliente.razon_social || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">CUIT</p>
                <p className="font-mono text-slate-900">{cliente.cuit || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">DNI</p>
                <p className="font-mono text-slate-900">{cliente.dni || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Email</p>
                <p className="text-slate-900">{cliente.email || cliente.email_waya || cliente.mail || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Estado</p>
                <Badge className={cliente.estado === 'Activa' ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-800'}>
                  {cliente.estado || 'Sin estado'}
                </Badge>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Tipo Persona</p>
                <p className="text-slate-900">{cliente.persona || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Fecha de Nacimiento</p>
                <p className="font-mono text-slate-900">{cliente.fecha_nacimiento || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Nacionalidad</p>
                <p className="text-slate-900">{cliente.nacionalidad || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Género</p>
                <p className="text-slate-900">
                  {cliente.genero === 'M' ? 'Masculino' : cliente.genero === 'F' ? 'Femenino' : cliente.genero === 'X' ? 'X' : cliente.genero || '-'}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">PEP</p>
                <Badge className={cliente.pep === 'SI' || cliente.pep_uif ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}>
                  {cliente.pep === 'SI' || cliente.pep_uif ? 'SI' : 'NO'}
                </Badge>
                {cliente.pep_uif && cliente.pep_fuente === 'reporte_usuarios_waya' && (
                  <p className="text-[11px] text-slate-500 mt-1" data-testid="cliente-pep-fuente">
                    Según Reporte de usuarios Waya{cliente.pep_marcado_en ? ` (${new Date(cliente.pep_marcado_en).toLocaleDateString('es-AR')})` : ''}
                  </p>
                )}
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Fecha Alta</p>
                <p className="font-mono text-slate-900">
                  {cliente.fecha_alta ? new Date(cliente.fecha_alta).toLocaleDateString('es-AR') : '-'}
                </p>
              </div>
            </div>
          </Card>

          {/* Impuesto al cheque */}
          <Card className="p-6 bg-white border-slate-200" data-testid="cliente-impuesto-cheque-card">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-indigo-50"><Receipt size={18} className="text-indigo-600" /></div>
                <div>
                  <h2 className="text-lg font-bold text-slate-900">Impuesto al cheque</h2>
                  <p className="text-sm text-slate-500 max-w-md">Controla si esta cuenta entra al cálculo del impuesto (Ley 25.413). La marca se comparte con Reportes › Impuesto al cheque.</p>
                  {icEstado && (
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      {icEstado.incluir
                        ? <Badge className="bg-emerald-100 text-emerald-800" data-testid="cliente-ic-estado">Entra al impuesto</Badge>
                        : <Badge className="bg-slate-100 text-slate-700" data-testid="cliente-ic-estado">No entra</Badge>}
                      {icEstado.exento && <Badge className="bg-rose-100 text-rose-800"><ShieldOff size={12} className="mr-1" /> Exenta</Badge>}
                      <Badge variant="secondary" className="bg-slate-100 text-slate-500">
                        {icEstado.origen === 'auto' ? 'Auto (CUIT 30/33/34)' : icEstado.origen === 'manual' ? 'Marca manual' : 'No jurídica'}
                      </Badge>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">Entra al impuesto</span>
                <Switch
                  checked={!!icEstado?.incluir}
                  disabled={!icEstado?.es_admin || icSaving}
                  onCheckedChange={toggleImpuestoCheque}
                  data-testid="cliente-ic-switch"
                />
              </div>
            </div>
            {icEstado?.exento && (
              <p className="text-xs text-rose-500 mt-3">Esta cuenta está marcada como EXENTA en el módulo de impuesto, por lo que no tributa aunque entre. Gestioná la exención desde Reportes › Impuesto al cheque.</p>
            )}
            {icEstado && !icEstado.es_admin && (
              <p className="text-xs text-slate-400 mt-3">Solo un administrador puede cambiar esta marca.</p>
            )}
          </Card>

          {/* B2 / B3: CVU activo y anteriores (física) o cuentas por sucursal (jurídica) */}
          <ClienteCuentas ficha={ficha} />

          {/* Sección CVUs */}
          <ClienteCvusTabla
            cvus={cvus}
            cvuPadron={cliente.cvu}
            esAdmin={esAdmin}
            onAdd={() => setIsAddCvuOpen(true)}
            onToggleActivo={handleToggleCvuActivo}
            onSetPrincipal={handleSetPrincipal}
            onDelete={handleDeleteCvu}
            onVincular={setCvuReemplazo}
          />

          <ClienteListaBlanca cuit={cliente.cuit_limpio || cuit} />
        </div>
      )}

      {/* Tab MRC */}
      {activeTab === 'mrc' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Score Result */}
          <Card className="p-6 bg-white border-slate-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-slate-900">Score MRC</h2>
              <Button
                size="sm"
                onClick={handleCalculateMRC}
                disabled={calculatingMRC}
                style={{ backgroundColor: '#2563eb', color: '#ffffff' }}
              >
                {calculatingMRC ? <RefreshCw size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                <span className="ml-1">Calcular</span>
              </Button>
            </div>

            {mrcScore && mrcScore.score_mrc !== null ? (
              <div className="space-y-4">
                {/* Score Combinado */}
                <div className="text-center p-4 rounded-lg" style={{ backgroundColor: getMRCCategoryColor(mrcScore.categoria_final).bg }}>
                  <p className="text-4xl font-bold" style={{ color: getMRCCategoryColor(mrcScore.categoria_final).text }}>
                    {mrcScore.score_combinado}
                  </p>
                  <p className="text-sm font-medium" style={{ color: getMRCCategoryColor(mrcScore.categoria_final).text }}>
                    Riesgo {mrcScore.categoria_final}
                  </p>
                </div>

                {/* Desglose */}
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between p-2 rounded bg-slate-50">
                    <span className="text-slate-600">Score MRC</span>
                    <span className="font-mono font-bold">{mrcScore.score_mrc} <span className="text-xs text-slate-400">({mrcScore.categoria_mrc})</span></span>
                  </div>
                  <div className="flex justify-between p-2 rounded bg-slate-50">
                    <span className="text-slate-600">Score Operativo</span>
                    <span className="font-mono font-bold">{mrcScore.score_operativo_normalizado} <span className="text-xs text-slate-400">({mrcScore.categoria_operativo})</span></span>
                  </div>
                </div>

                {/* DDR Warning */}
                {mrcScore.requiere_debida_diligencia_reforzada && (
                  <div className="p-3 rounded-lg bg-red-50 border border-red-200">
                    <div className="flex items-center gap-2 text-red-700 font-medium">
                      <AlertTriangle size={16} />
                      Requiere Debida Diligencia Reforzada
                    </div>
                    <p className="text-xs text-red-600 mt-1">{mrcScore.motivo_ddr}</p>
                  </div>
                )}

                {/* Detalle por factor */}
                <div className="mt-4">
                  <p className="text-xs text-slate-500 uppercase mb-2">Detalle por Factor</p>
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {mrcScore.detalle_factores?.map((f, idx) => (
                      <div key={idx} className="flex items-center justify-between text-xs p-1.5 rounded bg-slate-50">
                        <span className="text-slate-600 truncate flex-1">{f.factor_nombre}</span>
                        <span className={`px-1.5 py-0.5 rounded font-bold ${
                          f.valor_riesgo <= 2 ? 'bg-green-100 text-green-700' :
                          f.valor_riesgo === 3 ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>{f.valor_riesgo}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400">
                <ClipboardList size={40} className="mx-auto mb-2 opacity-50" />
                <p>Configure los atributos y calcule el score</p>
              </div>
            )}
          </Card>

          {/* Formulario de Atributos */}
          <Card className="lg:col-span-2 p-6 bg-white border-slate-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-slate-900">Atributos del Cliente</h2>
              <Button
                size="sm"
                onClick={handleSaveMRCAtributos}
                disabled={savingMRC}
                style={{ backgroundColor: '#10b981', color: '#ffffff' }}
              >
                <Save size={14} className="mr-1" />
                {savingMRC ? 'Guardando...' : 'Guardar'}
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* PEP Switch */}
              <div className="md:col-span-2 flex items-center justify-between p-3 rounded-lg bg-slate-50">
                <div>
                  <Label className="text-slate-900 font-medium">¿Es PEP?</Label>
                  <p className="text-xs text-slate-500">Persona Expuesta Políticamente</p>
                </div>
                <Switch
                  checked={mrcAtributos.es_pep || false}
                  onCheckedChange={(checked) => setMrcAtributos({ ...mrcAtributos, es_pep: checked })}
                />
              </div>

              {/* Factores */}
              {Object.entries(mrcFactores).map(([factorId, data]) => {
                const factor = data.factor;
                const opciones = data.opciones || [];
                
                // Mapeo de factor a campo de atributo
                const fieldMap = {
                  'factor-actividad': 'actividad_economica_id',
                  'factor-tipo-cliente': 'tipo_cliente_id',
                  'factor-pep': 'pep_tipo_id',
                  'factor-zona': 'zona_geografica_id',
                  'factor-antiguedad': 'antiguedad_id',
                  'factor-producto': 'producto_servicio_id',
                  'factor-volumen': 'volumen_transaccional_id',
                  'factor-metodo-ingreso': 'metodo_ingreso_fondos_id'
                };
                
                const field = fieldMap[factorId];
                if (!field) return null;

                // Si es PEP, solo mostrar cuando es_pep es true
                if (factorId === 'factor-pep' && !mrcAtributos.es_pep) return null;

                return (
                  <div key={factorId}>
                    <Label className="text-slate-700 text-sm">{factor.nombre}</Label>
                    <Select
                      value={mrcAtributos[field] || ''}
                      onValueChange={(value) => setMrcAtributos({ ...mrcAtributos, [field]: value })}
                    >
                      <SelectTrigger style={{ backgroundColor: '#ffffff' }}>
                        <SelectValue placeholder="Seleccionar..." />
                      </SelectTrigger>
                      <SelectContent>
                        {opciones.map(op => (
                          <SelectItem key={op.id} value={op.id}>
                            <div className="flex items-center gap-2">
                              <span className={`w-5 h-5 flex items-center justify-center rounded text-xs font-bold ${
                                op.valor_riesgo <= 2 ? 'bg-green-100 text-green-700' :
                                op.valor_riesgo === 3 ? 'bg-yellow-100 text-yellow-700' :
                                'bg-red-100 text-red-700'
                              }`}>{op.valor_riesgo}</span>
                              <span className="text-sm">{op.descripcion}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                );
              })}

              {/* Notas */}
              <div className="md:col-span-2">
                <Label className="text-slate-700 text-sm">Notas / Observaciones</Label>
                <Textarea
                  value={mrcAtributos.notas || ''}
                  onChange={(e) => setMrcAtributos({ ...mrcAtributos, notas: e.target.value })}
                  placeholder="Observaciones sobre el perfil de riesgo del cliente..."
                  rows={2}
                  style={{ backgroundColor: '#ffffff' }}
                />
              </div>
            </div>
          </Card>
        </div>
      )}

      {activeTab === 'alertas' && (
        <Card className="p-6 bg-white border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-slate-900">Historial de Alertas</h2>
            <Button
              size="sm"
              onClick={() => setIsCreateAlertOpen(true)}
              style={{ backgroundColor: '#dc2626', color: '#ffffff' }}
            >
              <Plus size={14} className="mr-1" />
              Nueva Alerta
            </Button>
          </div>
          
          {alertas.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <AlertTriangle size={40} className="mx-auto mb-2 text-slate-300" />
              <p>No hay alertas registradas para este cliente</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Severidad</TableHead>
                  <TableHead>Título</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {alertas.map(alert => (
                  <TableRow key={alert.id} className="cursor-pointer hover:bg-slate-50">
                    <TableCell>
                      <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-semibold ${getSeverityColor(alert.severidad)}`}>
                        {alert.severidad}
                      </span>
                    </TableCell>
                    <TableCell className="max-w-[300px]">
                      <p className="font-medium text-slate-900 truncate">{alert.titulo}</p>
                      <p className="text-xs text-slate-500 truncate">{alert.descripcion}</p>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{alert.estado}</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-slate-500 font-mono">
                      {new Date(alert.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/alerts/${alert.id}`)}
                      >
                        Ver
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      )}

      {/* Tab: Casos */}
      {activeTab === 'casos' && (
        <Card className="p-6 bg-white border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-slate-900">Casos Relacionados</h2>
            <Button
              size="sm"
              onClick={() => {
                setCaseFromMov({ 
                  titulo: `Caso - ${cliente?.razon_social || cuit}`, 
                  descripcion: '', 
                  prioridad: 'MEDIA',
                  tipo: 'ALERTA'
                });
                setIsCreateCaseFromMovOpen(true);
              }}
              style={{ backgroundColor: '#7c3aed', color: '#ffffff' }}
            >
              <Plus size={14} className="mr-1" />
              Nuevo Caso
            </Button>
          </div>
          
          {casos.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Briefcase size={40} className="mx-auto mb-2 text-slate-300" />
              <p>No hay casos relacionados con este cliente</p>
              <p className="text-xs mt-1">Seleccione movimientos para crear un caso de investigación</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Título</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Prioridad</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {casos.map(caso => (
                  <TableRow key={caso.id}>
                    <TableCell>
                      <span className="font-medium text-slate-900">{caso.titulo}</span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={
                        caso.tipo === 'FRAUDE' ? 'border-red-300 text-red-700 bg-red-50' :
                        caso.tipo === 'SOPORTE' ? 'border-blue-300 text-blue-700 bg-blue-50' :
                        caso.tipo === 'COMPLIANCE' ? 'border-purple-300 text-purple-700 bg-purple-50' :
                        'border-orange-300 text-orange-700 bg-orange-50'
                      }>
                        {caso.tipo || 'ALERTA'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={
                        caso.estado === 'ABIERTO' ? 'border-blue-300 text-blue-700 bg-blue-50' :
                        caso.estado === 'EN_INVESTIGACION' ? 'border-yellow-300 text-yellow-700 bg-yellow-50' :
                        caso.estado === 'CERRADO' ? 'border-green-300 text-green-700 bg-green-50' :
                        'border-slate-300 text-slate-700 bg-slate-50'
                      }>
                        {caso.estado?.replace('_', ' ') || 'ABIERTO'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={
                        caso.prioridad === 'CRITICA' ? 'border-red-300 text-red-700 bg-red-50' :
                        caso.prioridad === 'ALTA' ? 'border-orange-300 text-orange-700 bg-orange-50' :
                        caso.prioridad === 'MEDIA' ? 'border-yellow-300 text-yellow-700 bg-yellow-50' :
                        'border-green-300 text-green-700 bg-green-50'
                      }>
                        {caso.prioridad || 'MEDIA'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-600 text-sm">
                      {new Date(caso.fecha_creacion).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/m/gestion-de-casos/caso/${caso.id}`)}
                      >
                        <Eye size={14} className="mr-1" />
                        Ver
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      )}

      {activeTab === 'movimientos' && (
        <ClienteMovimientos cuit={cuitDigitos} rango={rango} setRango={setRango} />
      )}

      {activeTab === 'analisis' && (
        <ClienteAnalisis cuit={cuitDigitos} rango={rango} perfilDeclarado={ficha?.perfil_declarado} />
      )}

      {activeTab === 'documentos' && (
        <DocumentManager entityType="client" entityId={cuit} />
      )}

      {activeTab === 'legajo' && (
        <ClienteLegajoDocumentos clienteCuit={cuit} />
      )}

      {activeTab === 'estado' && (
        <ClienteEstadoManager 
          clienteCuit={cuit} 
          clienteNombre={cliente?.razon_social || cliente?.nombre} 
        />
      )}

      {/* Tab Empresa: B3 reutiliza las pestañas de EmpresaDetalle (documentación, representante legal, cuentas, límites) */}
      {activeTab === 'empresa' && empresaData && (
        <div data-testid="tab-empresa-content">
          <EmpresaDetalle empresaIdProp={empresaData.id} embebido />
        </div>
      )}

      {/* Modal: Crear Alerta */}
      <Dialog open={isCreateAlertOpen} onOpenChange={setIsCreateAlertOpen}>
        <DialogContent style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#0f172a' }}>Crear Nueva Alerta</DialogTitle>
            <DialogDescription style={{ color: '#64748b' }}>
              Crear una alerta manual para el cliente {cuit}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label style={{ color: '#334155' }}>Título *</Label>
              <Input
                value={newAlert.titulo}
                onChange={(e) => setNewAlert({ ...newAlert, titulo: e.target.value })}
                placeholder="Describe brevemente la alerta..."
                style={{ backgroundColor: '#f8fafc', color: '#0f172a' }}
              />
            </div>
            <div>
              <Label style={{ color: '#334155' }}>Descripción</Label>
              <Textarea
                value={newAlert.descripcion}
                onChange={(e) => setNewAlert({ ...newAlert, descripcion: e.target.value })}
                placeholder="Detalles adicionales..."
                rows={3}
                style={{ backgroundColor: '#f8fafc', color: '#0f172a' }}
              />
            </div>
            <div>
              <Label style={{ color: '#334155' }}>Severidad</Label>
              <Select
                value={newAlert.severidad}
                onValueChange={(value) => setNewAlert({ ...newAlert, severidad: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SEVERITIES.map(sev => (
                    <SelectItem key={sev} value={sev}>{sev}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateAlertOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleCreateAlert}
              disabled={savingAlert}
              style={{ backgroundColor: '#dc2626', color: '#ffffff' }}
            >
              {savingAlert ? 'Creando...' : 'Crear Alerta'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Ejecutar Monitor */}
      <Dialog open={isRunMonitorOpen} onOpenChange={setIsRunMonitorOpen}>
        <DialogContent style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#0f172a' }}>Ejecutar Monitor sobre Cliente</DialogTitle>
            <DialogDescription style={{ color: '#64748b' }}>
              Seleccione un monitor para analizar este cliente
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label style={{ color: '#334155' }}>Monitor</Label>
              <Select
                value={selectedMonitor || ''}
                onValueChange={setSelectedMonitor}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccione un monitor..." />
                </SelectTrigger>
                <SelectContent>
                  {monitors.map(m => (
                    <SelectItem key={m.id} value={m.id}>
                      <div className="flex flex-col">
                        <span>{m.nombre}</span>
                        <span className="text-xs text-slate-500">{m.descripcion}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-slate-500">
              El monitor se ejecutará y si encuentra coincidencias para este cliente, 
              se generará una alerta automáticamente.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRunMonitorOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleRunMonitor}
              disabled={runningMonitor || !selectedMonitor}
              style={{ backgroundColor: '#9333ea', color: '#ffffff' }}
            >
              {runningMonitor ? 'Ejecutando...' : 'Ejecutar Monitor'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <VincularReemplazoDialog
        open={!!cvuReemplazo}
        onOpenChange={(o) => { if (!o) setCvuReemplazo(null); }}
        cuit={cliente?.cuit_limpio || cuit}
        cvu={cvuReemplazo}
        cvus={cvus}
        onSaved={loadClienteData}
      />

      {/* Modal: Agregar CVU */}
      <Dialog open={isAddCvuOpen} onOpenChange={setIsAddCvuOpen}>
        <DialogContent style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: '#0f172a' }}>
              <CreditCard size={20} className="text-blue-600" />
              Agregar Nuevo CVU
            </DialogTitle>
            <DialogDescription style={{ color: '#64748b' }}>
              Agregar un CVU adicional al cliente {cliente?.razon_social || cuit}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label style={{ color: '#334155' }}>CVU *</Label>
              <Input
                value={newCvu.cvu}
                onChange={(e) => setNewCvu({ ...newCvu, cvu: e.target.value })}
                placeholder="Ej: 0000357300000000012345"
                maxLength={30}
                style={{ backgroundColor: '#f8fafc', color: '#0f172a' }}
              />
            </div>
            <div>
              <Label style={{ color: '#334155' }}>Alias (opcional)</Label>
              <Input
                value={newCvu.alias}
                onChange={(e) => setNewCvu({ ...newCvu, alias: e.target.value })}
                placeholder="Ej: Cuenta Secundaria, Cuenta USD..."
                style={{ backgroundColor: '#f8fafc', color: '#0f172a' }}
              />
            </div>
            <div>
              <Label style={{ color: '#334155' }}>Tipo</Label>
              <Select
                value={newCvu.tipo}
                onValueChange={(value) => setNewCvu({ ...newCvu, tipo: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="SECUNDARIO">Secundario</SelectItem>
                  <SelectItem value="PRINCIPAL">Principal</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                Si selecciona &quot;Principal&quot;, el CVU actual será cambiado a Secundario
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddCvuOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleAddCvu}
              disabled={savingCvu || !newCvu.cvu.trim()}
              style={{ backgroundColor: '#2563eb', color: '#ffffff' }}
            >
              {savingCvu ? 'Guardando...' : 'Agregar CVU'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Crear Alerta desde Movimientos */}
      <Dialog open={isCreateAlertFromMovOpen} onOpenChange={setIsCreateAlertFromMovOpen}>
        <DialogContent style={{ backgroundColor: '#ffffff', maxWidth: '500px' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: '#0f172a' }}>
              <AlertTriangle size={20} className="text-red-600" />
              Crear Alerta desde Movimientos
            </DialogTitle>
            <DialogDescription style={{ color: '#64748b' }}>
              {selectedMovimientos.size} movimiento(s) seleccionado(s)
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label style={{ color: '#334155' }}>Título *</Label>
              <Input
                value={alertFromMov.titulo}
                onChange={(e) => setAlertFromMov({ ...alertFromMov, titulo: e.target.value })}
                placeholder="Describe brevemente la alerta..."
                style={{ backgroundColor: '#f8fafc', color: '#0f172a' }}
              />
            </div>
            <div>
              <Label style={{ color: '#334155' }}>Descripción</Label>
              <Textarea
                value={alertFromMov.descripcion}
                onChange={(e) => setAlertFromMov({ ...alertFromMov, descripcion: e.target.value })}
                placeholder="Detalles adicionales..."
                rows={4}
                style={{ backgroundColor: '#f8fafc', color: '#0f172a' }}
              />
            </div>
            <div>
              <Label style={{ color: '#334155' }}>Severidad</Label>
              <Select
                value={alertFromMov.severidad}
                onValueChange={(value) => setAlertFromMov({ ...alertFromMov, severidad: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SEVERITIES.map(sev => (
                    <SelectItem key={sev} value={sev}>{sev}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateAlertFromMovOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleCreateAlertFromMovimientos}
              disabled={savingAlert || !alertFromMov.titulo.trim()}
              style={{ backgroundColor: '#dc2626', color: '#ffffff' }}
            >
              {savingAlert ? 'Creando...' : 'Crear Alerta'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Crear Caso desde Movimientos */}
      <Dialog open={isCreateCaseFromMovOpen} onOpenChange={setIsCreateCaseFromMovOpen}>
        <DialogContent className="max-w-lg" style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Briefcase className="text-purple-600" />
              Crear Caso de Investigación
            </DialogTitle>
            <DialogDescription>
              {selectedMovimientos.size > 0 
                ? `Crear caso vinculado a ${selectedMovimientos.size} movimiento(s) seleccionado(s)`
                : 'Crear un nuevo caso para este cliente'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Título del Caso *</Label>
              <Input
                value={caseFromMov.titulo}
                onChange={(e) => setCaseFromMov(c => ({ ...c, titulo: e.target.value }))}
                placeholder="Ej: Investigación transacciones sospechosas"
                style={{ backgroundColor: '#fff' }}
              />
            </div>

            <div>
              <Label>Descripción</Label>
              <Textarea
                value={caseFromMov.descripcion}
                onChange={(e) => setCaseFromMov(c => ({ ...c, descripcion: e.target.value }))}
                placeholder="Describa el caso..."
                rows={3}
                style={{ backgroundColor: '#fff' }}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tipo de Caso</Label>
                <Select
                  value={caseFromMov.tipo}
                  onValueChange={(v) => setCaseFromMov(c => ({ ...c, tipo: v }))}
                >
                  <SelectTrigger style={{ backgroundColor: '#fff' }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="FRAUDE">Fraude</SelectItem>
                    <SelectItem value="SOPORTE">Soporte</SelectItem>
                    <SelectItem value="ALERTA">Alerta</SelectItem>
                    <SelectItem value="COMPLIANCE">Compliance</SelectItem>
                    <SelectItem value="OTRO">Otro</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Prioridad</Label>
                <Select
                  value={caseFromMov.prioridad}
                  onValueChange={(v) => setCaseFromMov(c => ({ ...c, prioridad: v }))}
                >
                  <SelectTrigger style={{ backgroundColor: '#fff' }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CRITICA">Crítica</SelectItem>
                    <SelectItem value="ALTA">Alta</SelectItem>
                    <SelectItem value="MEDIA">Media</SelectItem>
                    <SelectItem value="BAJA">Baja</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {selectedMovimientos.size > 0 && (
              <div className="p-3 bg-purple-50 rounded-lg text-sm">
                <p className="font-medium text-purple-800">
                  Movimientos seleccionados: {selectedMovimientos.size}
                </p>
                <p className="text-xs text-purple-600 mt-1">
                  IDs: {Array.from(selectedMovimientos).slice(0, 5).join(', ')}
                  {selectedMovimientos.size > 5 && '...'}
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateCaseFromMovOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleCreateCaseFromMovimientos}
              disabled={savingCase || !caseFromMov.titulo.trim()}
              style={{ backgroundColor: '#7c3aed', color: '#ffffff' }}
            >
              {savingCase ? 'Creando...' : 'Crear Caso'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Editar Cliente */}
      <Dialog open={isEditClienteOpen} onOpenChange={setIsEditClienteOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Editar Cliente</DialogTitle>
            <DialogDescription>Modificar datos de {cliente?.razon_social}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2">
              <Label>Razón Social</Label>
              <Input value={editForm.razon_social || ''} onChange={e => setEditForm(f => ({...f, razon_social: e.target.value}))} data-testid="edit-razon-social" />
            </div>
            <div>
              <Label>DNI</Label>
              <Input value={editForm.dni || ''} onChange={e => setEditForm(f => ({...f, dni: e.target.value}))} data-testid="edit-dni" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={editForm.email || ''} onChange={e => setEditForm(f => ({...f, email: e.target.value}))} data-testid="edit-email" />
            </div>
            <div>
              <Label>Fecha de Nacimiento</Label>
              <Input placeholder="DD/MM/AAAA" value={editForm.fecha_nacimiento || ''} onChange={e => setEditForm(f => ({...f, fecha_nacimiento: e.target.value}))} data-testid="edit-fecha-nacimiento" />
            </div>
            <div>
              <Label>Nacionalidad</Label>
              <Input placeholder="Ej: ARG" value={editForm.nacionalidad || ''} onChange={e => setEditForm(f => ({...f, nacionalidad: e.target.value.toUpperCase()}))} data-testid="edit-nacionalidad" />
            </div>
            <div>
              <Label>Género</Label>
              <Select value={editForm.genero || ''} onValueChange={v => setEditForm(f => ({...f, genero: v}))}>
                <SelectTrigger data-testid="edit-genero">
                  <SelectValue placeholder="Seleccionar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="M">Masculino</SelectItem>
                  <SelectItem value="F">Femenino</SelectItem>
                  <SelectItem value="X">X</SelectItem>
                  <SelectItem value="O">Otro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tipo</Label>
              <Select value={editForm.tipo || ''} onValueChange={v => setEditForm(f => ({...f, tipo: v}))}>
                <SelectTrigger data-testid="edit-tipo">
                  <SelectValue placeholder="Seleccionar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Persona Física">Persona Física</SelectItem>
                  <SelectItem value="Persona Jurídica">Persona Jurídica</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Teléfono</Label>
              <Input value={editForm.telefono || ''} onChange={e => setEditForm(f => ({...f, telefono: e.target.value}))} data-testid="edit-telefono" />
            </div>
            <div className="col-span-2 flex items-center gap-3">
              <Switch checked={editForm.pep_uif || false} onCheckedChange={v => setEditForm(f => ({...f, pep_uif: v}))} data-testid="edit-pep" />
              <Label>Es PEP (Persona Expuesta Políticamente)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditClienteOpen(false)}>Cancelar</Button>
            <Button onClick={handleSaveCliente} disabled={savingCliente} data-testid="save-cliente-btn">
              {savingCliente ? <RefreshCw size={14} className="animate-spin mr-1" /> : <Save size={14} className="mr-1" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClienteDetalle;
